/**
 * The dialogs on a storage location's card: restores, quota, removal, credentials. Moving files out is a storage
 * migration (1.1.0: Storage → Migrations); the old Move files dialog is gone.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, KeyRound, Loader2, Save, Trash2 } from 'lucide-react';
import type { LocationRemovalImpact, StorageLocationSummary } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { Modal } from '../../../components/Modal';
import { toast } from '../../../components/Toasts';
import { bytes } from '../../../lib/format';
import { queryKeys } from '../../../api/queryKeys';
import { useCategories } from '../../../api/hooks';
import { WorkstationMappingsEditor, mappingProblems, mappingsBody, type MappingDraft } from '../../../components/WorkstationMappings';

/** An archive's restore defaults: how long restored files stay readable, and the retrieval tier (AWS). */
export function RestoreSettingsDialog({ loc, onClose }: { loc: StorageLocationSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [days, setDays] = useState(String(loc.archive?.restoreDays ?? 3));
  const [tier, setTier] = useState(loc.archive?.restoreTier ?? 'Standard');
  const [saving, setSaving] = useState(false);
  const max = loc.kind === 'oci' ? 10 : 30;
  async function save() {
    setSaving(true);
    try {
      await api(`/api/v1/storage-locations/${loc.id}`, { method: 'PATCH', json: { restoreDays: Number(days), ...(loc.archive?.restoreTiers.length ? { restoreTier: tier } : {}) } });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      toast('Restore settings saved');
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal title={`Restores from ${loc.name}`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={saving || !(Number(days) >= 1 && Number(days) <= max)} onClick={() => void save()}><Save size={14} /> Save</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>A restore makes an archived original readable for a while; PRISM copies it to the originals tier as soon as it is, so a short time is enough.</p>
      <div className="field"><label htmlFor="restore-days">Readable for (days)</label>
        <input id="restore-days" className="input" type="number" min={1} max={max} value={days} onChange={(e) => setDays(e.target.value)} />
        {loc.kind === 'oci' && <span className="help">OCI keeps a restored object for at most 240 hours (10 days).</span>}</div>
      {(loc.archive?.restoreTiers.length ?? 0) > 0 && (
        <div className="field"><label htmlFor="restore-tier-default">Default retrieval</label>
          <select id="restore-tier-default" className="select" value={tier} onChange={(e) => setTier(e.target.value)}>
            {loc.archive!.restoreTiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="help">Expedited: 1–5 minutes (Glacier Flexible Retrieval). Standard: 3–5 hours (Deep Archive up to 12). Bulk: 5–12 hours (Deep Archive up to 48), the cheapest.</span></div>
      )}
    </Modal>
  );
}

const GB = 1e9;

/** A hard limit on what PRISM keeps in a location. */
export function QuotaDialog({ loc, onClose }: { loc: StorageLocationSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [on, setOn] = useState(Boolean(loc.quota));
  const [gb, setGb] = useState(loc.quota ? String(Math.round((loc.quota.limitBytes / GB) * 100) / 100) : '');
  const [busy, setBusy] = useState(false);
  const limit = Math.round(Number(gb) * GB);
  const valid = !on || (Number.isFinite(limit) && limit > 0);
  async function save() {
    setBusy(true);
    try {
      await api(`/api/v1/storage-locations/${loc.id}`, { method: 'PATCH', json: { quotaBytes: on ? limit : null } });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      toast(on ? `Quota for ${loc.name} set to ${bytes(limit)}` : `Quota for ${loc.name} removed`);
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={`Quota for ${loc.name}`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!valid || busy} onClick={() => void save()} data-testid="save-quota">Save</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>
        PRISM never keeps more than the quota here. Uploads, watch folder ingests, attachments, transfers and tiering rules that would go over it are refused;
        when the tier is full, new ingests and exports for it wait until space is freed. Storage and System status warn from 90%.
      </p>
      <label className="toggle-row"><input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid="quota-on" /><span>Limit what PRISM keeps here</span></label>
      {on && <div className="field" style={{ marginTop: 10 }}><label htmlFor="quota-gb">Quota (GB)</label>
        <input id="quota-gb" className="input" type="number" min={0} step="any" value={gb} onChange={(e) => setGb(e.target.value)} data-testid="quota-gb" />
        <span className="help">PRISM keeps {bytes(loc.usage.bytes)} here now{loc.diskCapacity ? `; the disk has ${bytes(loc.diskCapacity.freeBytes)} free` : ''}.</span></div>}
    </Modal>
  );
}

/** Removal always warns first: what it affects, what stops it, and a typed confirmation. */
export function RemoveDialog({ loc, onClose }: { loc: StorageLocationSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const impact = useQuery({ queryKey: ['removal', loc.id], queryFn: () => api<LocationRemovalImpact>(`/api/v1/storage-locations/${loc.id}/removal`) });
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const i = impact.data;
  const blocked = Boolean(i?.blocking.length);
  const where = loc.kind === 's3' || loc.kind === 'oci' ? 'bucket' : 'folder';
  async function remove() {
    setBusy(true);
    try {
      await api(`/api/v1/storage-locations/${loc.id}?confirm=${encodeURIComponent(typed)}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      // An export location's removal changes what the export dialog offers.
      if (loc.purpose === 'export') await qc.invalidateQueries({ queryKey: queryKeys.exportDestinations() });
      toast(`Removed ${loc.name}`);
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={`Remove ${loc.name}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{blocked ? 'Close' : 'Cancel'}</button>
      {!blocked && <button className="btn danger" disabled={!i || typed !== loc.name || busy} onClick={() => void remove()} data-testid="confirm-remove"><Trash2 size={14} /> Remove {loc.purpose === 'export' ? 'export location' : 'location'}</button>}
    </>}>
      {!i ? <div className="skeleton" style={{ height: 80 }} /> : blocked ? (
        <ErrorBanner testId="removal-blocked" title="It cannot be removed yet">
          {i.blocking.map((b) => <span key={b}>{b}</span>)}
          {i.files > 0 && loc.purpose === 'storage' && <span>Move its files elsewhere with a migration: <Link to={`/admin/storage?view=migrations&new=1&source=${loc.id}`} onClick={onClose} data-testid="removal-migrate"><ArrowRightLeft size={12} style={{ verticalAlign: '-1px' }} /> Storage → Migrations</Link>.</span>}</ErrorBanner>
      ) : (
        <>
          <div className="banner warning" data-testid="removal-warning"><div className="banner-body">
            <strong>PRISM will stop using this {where}</strong>
            <span>It disappears from storage tiers{loc.purpose === 'export' ? ', from the export dialog' : ''} and from this page. The {where} itself and every file in it are left untouched.</span>
            {i.exports > 0 && <span>{i.exports} finished export{i.exports === 1 ? ' was' : 's were'} stored or delivered here; {i.exports === 1 ? 'it' : 'they'} will no longer play or download in PRISM.</span>}
          </div></div>
          <div className="field"><label htmlFor="confirm-name">Type <strong>{loc.name}</strong> to confirm</label>
            <input id="confirm-name" className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" data-testid="confirm-name" /></div>
        </>
      )}
    </Modal>
  );
}

export function CredentialsDialog({ loc, onClose }: { loc: StorageLocationSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [keyId, setKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api(`/api/v1/storage-locations/${loc.id}`, { method: 'PATCH', json: { accessKeyId: keyId.trim(), secretAccessKey: secret } });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      toast('Credentials replaced');
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={`Credentials for ${loc.name}`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!keyId.trim() || !secret || busy} onClick={() => void save()}>{busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />} Test and save</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>The new key is tried against the bucket before it replaces the old one. The secret is stored encrypted and never shown again.</p>
      <div className="field"><label htmlFor="cred-id">Access key</label><input id="cred-id" className="input mono" value={keyId} onChange={(e) => setKeyId(e.target.value)} autoComplete="off" /></div>
      <div className="field"><label htmlFor="cred-secret">Secret key</label><input id="cred-secret" className="input mono" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" /></div>
    </Modal>
  );
}

/**
 * The workstation mappings of an edit location (with its retention and check-in watch), or of a storage location
 * whose originals hand-offs link in place (1.0.0-rc.9; rc.6 had one path per operating system).
 */
export function WorkstationPathsDialog({ loc, onClose }: { loc: StorageLocationSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [mappings, setMappings] = useState<MappingDraft[]>(loc.workstationMappings ?? []);
  const [days, setDays] = useState(String(loc.edit?.retentionDays ?? 30));
  const [watch, setWatch] = useState(Boolean(loc.edit?.checkinWatch));
  const [processOn, setProcessOn] = useState<'agent' | 'central'>(loc.edit?.processOn === 'central' ? 'central' : 'agent');
  // Asset classes of what a check-in brings in (fixes after rc.9 testing): the same classes as watch folders.
  const [mediaClass, setMediaClass] = useState(loc.edit?.checkinCategoryId ?? '');
  const [masterClass, setMasterClass] = useState(loc.edit?.masterCategoryId ?? '');
  const categories = useCategories().data?.items ?? [];
  const [saving, setSaving] = useState(false);
  const edit = loc.purpose === 'edit';
  /** Only edit locations on a storage agent choose where checked-in media is processed (fixes after rc.9 testing). */
  const agentEdit = edit && loc.kind === 'agent';
  async function save() {
    setSaving(true);
    try {
      await api(`/api/v1/storage-locations/${loc.id}`, { method: 'PATCH', json: { workstationMappings: mappingsBody(mappings), ...(edit ? { retentionDays: Number(days), checkinWatch: watch, checkinCategoryId: mediaClass || null, masterCategoryId: masterClass || null } : {}), ...(agentEdit ? { processOn } : {}) } });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      await qc.invalidateQueries({ queryKey: queryKeys.editDestinations() });
      toast('Saved');
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal title={`${edit ? 'Edit location' : 'Workstation mappings'}: ${loc.name}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={saving || (edit && !(Number(days) >= 1)) || mappingProblems(mappings).length > 0} onClick={() => void save()} data-testid="save-workstation-paths"><Save size={14} /> Save</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>
        {edit
          ? 'Where editors’ computers open this location, shared with them read-write. Send to edit writes the chosen mapping’s paths into the timelines, so they link without relinking.'
          : 'Editors can open these originals at… (for hand-offs that link the originals in place). Share it with editors read-only: PRISM’s integrity check flags any file that changes.'}
        {(loc.kind === 's3' || loc.kind === 'oci') && ' A bucket is opened as a share on a NAS that serves the same data over S3 and SMB.'}
      </p>
      <WorkstationMappingsEditor value={mappings} onChange={setMappings} purpose={edit ? 'edit' : 'storage'} />
      {edit && (
        <div className="field"><label htmlFor="edit-retention">Keep hand-offs for (days)</label>
          <input id="edit-retention" className="input" type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} style={{ maxWidth: 140 }} />
          <span className="help">After check-in or the last activity. Then System status → Housekeeping lists the folder for cleanup, which removes only what PRISM wrote there, once you confirm.</span></div>
      )}
      {edit && (
        <div className="field"><label>Check in</label>
          <label className="toggle-row"><input type="checkbox" checked={watch} onChange={(e) => setWatch(e.target.checked)} data-testid="edit-checkin-watch" />
            <span>Watch each open hand-off's <span className="mono">_to_prism/</span> folder, and tell the sender when an edited timeline is there</span></label>
          <span className="help">A light look every minute. It never imports anything on its own: the sender checks in from the note.</span></div>
      )}
      {edit && (
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 240px' }}><label htmlFor="edit-media-class">Asset class for new media from check-in</label>
            <select id="edit-media-class" className="select" value={mediaClass} onChange={(e) => setMediaClass(e.target.value)} data-testid="edit-media-class">
              <option value="">None</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="help">New clips and the placeholders for clips not in PRISM get it, with its fields, as from a watch folder.</span></div>
          <div className="field" style={{ flex: '1 1 240px' }}><label htmlFor="edit-master-class">Asset class for rendered masters</label>
            <select id="edit-master-class" className="select" value={masterClass} onChange={(e) => setMasterClass(e.target.value)} data-testid="edit-master-class">
              <option value="">None</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
        </div>
      )}
      {edit && agentEdit && (
        <div className="field"><label>Checked-in media</label>
          <div className="segmented" role="group" aria-label="Where checked-in media is probed and proxied">
            <button type="button" aria-pressed={processOn === 'agent'} onClick={() => setProcessOn('agent')} data-testid="edit-process-agent">On the storage agent</button>
            <button type="button" aria-pressed={processOn === 'central'} onClick={() => setProcessOn('central')} data-testid="edit-process-central">Centrally</button>
          </div>
          <span className="help">{processOn === 'agent'
            ? 'The storage agent makes the probe and proxy from the file in the edit folder, as agent watch folders do; PRISM makes the poster, scrub images and waveform from the proxy.'
            : 'The file is uploaded first, and PRISM’s workers make the probe and proxy from the copy.'}</span></div>
      )}
    </Modal>
  );
}
